import { Stack, Typography, Box, Button, TextField } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { SaveOutlined } from '@mui/icons-material';
import NamedEntities from './NamedEntities';
import { getTagName } from '@/utils/tagUtils';
import { InitialAssessment } from '@/services/noteGoApi/noteGoApiSchemas';

interface ContentAnalysisProps {
  initialAssessment: InitialAssessment;
  isOwner?: boolean;
  isProcessing?: boolean;
  isLoadingNote?: boolean;
  onUpdate?: (assessment: InitialAssessment) => Promise<void>;
}

const ENTITY_KEY_HINTS = ['entities', 'people', 'organizations', 'locations', 'companies', 'brands', 'topics', 'names'];

const isEntityKey = (key: string) => ENTITY_KEY_HINTS.some((hint) => key.toLowerCase().includes(hint));

const valueToString = (value: unknown): string => {
  if (Array.isArray(value)) return value.map((v) => getTagName(v)).join(', ');
  if (value && typeof value === 'object') return getTagName(value);
  return (value as string | null | undefined) ?? '';
};

const toEditableMap = (assessment: InitialAssessment): Record<string, string> => {
  const map: Record<string, string> = {};
  Object.entries(assessment).forEach(([key, value]) => {
    map[key] = valueToString(value);
  });
  return map;
};

const ContentAnalysis: FC<ContentAnalysisProps> = ({
  initialAssessment,
  isOwner = false,
  isProcessing = false,
  isLoadingNote = false,
  onUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [edited, setEdited] = useState<Record<string, string>>(() => toEditableMap(initialAssessment));

  // Re-sync local edits with persisted data whenever we're not actively editing
  useEffect(() => {
    if (!isEditing) setEdited(toEditableMap(initialAssessment));
  }, [initialAssessment, isEditing]);

  const showEditButton = isOwner && !isProcessing && !isLoadingNote && !!onUpdate;

  const handleEditOrSave = async () => {
    if (!isEditing) {
      setEdited(toEditableMap(initialAssessment));
      setIsEditing(true);
      return;
    }
    if (!onUpdate) {
      setIsEditing(false);
      return;
    }

    // Preserve original key set; overwrite values with edited strings. Entity
    // arrays are intentionally flattened to comma-separated strings (backend
    // accepts string | string[] | null for named_entities).
    // Normalize '' back to null so nullable fields aren't persisted as empty strings.
    const next: Record<string, unknown> = { ...initialAssessment };
    Object.entries(edited).forEach(([key, value]) => {
      next[key] = value === '' ? null : value;
    });

    setIsSaving(true);
    try {
      await onUpdate(next as InitialAssessment);
      setIsEditing(false);
    } catch {
      // onUpdate already surfaces its own alert — keep edit mode open for retry
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 4, maxWidth: '800px', width: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          variant="button"
          component="div"
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            color: 'text.primary',
            fontWeight: 600,
          }}
        >
          Content Analysis
        </Typography>
        {showEditButton && (
          <Button
            variant="text"
            color="primary"
            startIcon={isEditing ? <SaveOutlined /> : <EditOutlinedIcon />}
            onClick={handleEditOrSave}
            disabled={isSaving}
          >
            {isEditing ? (isSaving ? 'Saving…' : 'Save Changes') : 'Edit'}
          </Button>
        )}
      </Stack>
      <Stack spacing={isEditing ? 1.5 : 0.4} mt={1}>
        {Object.entries(initialAssessment).map(([key, value]) => {
          const label = key
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
          const entityField = isEntityKey(key);

          if (isEditing) {
            return (
              <TextField
                key={key}
                fullWidth
                size="small"
                label={label}
                value={edited[key] ?? ''}
                onChange={(e) => setEdited((prev) => ({ ...prev, [key]: e.target.value }))}
                helperText={entityField ? 'Comma-separated' : undefined}
                disabled={isSaving}
              />
            );
          }

          const stringValue = valueToString(value);
          return (
            <Box key={key}>
              <Typography
                variant="body2"
                component="div"
                color="textPrimary"
                sx={{
                  fontSize: '14px',
                  lineHeight: 1.43,
                  letterSpacing: '0.4px',
                }}
              >
                – {label}: {entityField ? <NamedEntities entitiesString={stringValue} /> : stringValue}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ContentAnalysis;
